import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import {
  addPrerequisite,
  deleteTask,
  descendantIds,
  hydrateSeed,
  isPendingTask,
  removePrerequisite,
  updateTask,
  type Task,
  type TaskAttachment,
  type TaskInput,
  type TaskRef,
  type TaskSummary,
} from '../../lib/tasks';
import { evictTask, fetchTask, getCachedTask, putTask } from '../../lib/taskCache';
import { TASK_STATUS_LABELS } from '../../lib/taskStatus';
import type { Assignee } from '../../lib/assignees';
import { Alert, Button, Input, SkeletonRows, Textarea } from '../ui';
import { TaskStatusBadge } from './TaskBadges';
import { TaskCombobox } from './TaskCombobox';
import { TaskComposer } from './TaskComposer';
import { TaskFields } from './TaskFields';
import { TaskComments } from './TaskComments';
import { TaskAttachments } from './TaskAttachments';
import { TaskChecklist } from './TaskChecklist';
import { TaskTimeLog } from './TaskTimeLog';
import { PanelHeader, useSidePanelClose } from './SidePanel';
import { BlockedIcon, TrashIcon } from '../icons';
import { useToast } from '../toast';

/**
 * The panel body for a task that exists. Rendered inside SidePanel, which owns
 * the overlay, the focus trap and Escape.
 */
export function TaskDetail({
  taskId,
  seed,
  tasks,
  assignees,
  tagSuggestions,
  titleId,
  hrefFor,
  createInBackground,
  onTaskChanged,
  onTaskDeleted,
}: {
  taskId: string;
  seed: TaskSummary | undefined;
  tasks: TaskSummary[];
  assignees: Assignee[];
  tagSuggestions: string[];
  titleId: string;
  hrefFor: (taskId: string) => string;
  /** Posts a task in the background and hands back the id the list is already
   *  showing it under, so the subtask list here can show the same one. The page
   *  owns the request and reports any failure. */
  createInBackground: (
    draft: Partial<TaskInput> & { title: string },
    options?: { reportFailure?: boolean },
  ) => { pendingId: string; result: Promise<Task | null> };
  onTaskChanged: (task: Task) => void;
  onTaskDeleted: (taskId: string) => void;
}) {
  // Closing goes through the shell so the panel slides out. Calling the caller's
  // navigate directly would teleport, which is what the X button used to do
  // while Escape and the scrim animated.
  const onClose = useSidePanelClose();
  const { toast } = useToast();
  /** The task as the server last confirmed it. */
  const [saved, setSaved] = useState<Task | null>(null);
  /** Field edits that have been sent but not answered yet. */
  const [pending, setPending] = useState<Partial<TaskInput>>({});
  const [loadError, setLoadError] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);
  /** Counted rather than a flag: two quick edits overlap, and the first to
   *  come back would otherwise clear "Saving…" while the second is still out. */
  const [inFlight, setInFlight] = useState(0);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  /** A subtask whose create failed, kept so it can be sent again from here. One
   *  at a time: a second failure replaces it, and both were the same dropped
   *  connection. */
  const [failedSubtask, setFailedSubtask] = useState<string | null>(null);
  /** Stamps each save, so a reply that has been overtaken can be ignored. */
  const latestSave = useRef(0);
  /**
   * Counts the sections below report once they have loaded their own contents.
   *
   * Kept apart from `saved` because they can arrive first: those sections now
   * fetch alongside the detail rather than after it, so writing them into the
   * task would mean writing into a task that is not there yet.
   */
  const [counts, setCounts] = useState<{ trackedSeconds?: number; commentCount?: number }>({});
  /**
   * Subtasks added from this panel that the loaded detail does not know about.
   *
   * Layered rather than spliced into `saved` so an addition cannot be lost to
   * the ordering of two replies: the detail request is already in flight when
   * the panel opens, and it answers for the task as it was before this.
   */
  const [addedSubtasks, setAddedSubtasks] = useState<TaskRef[]>([]);

  /**
   * Fetch the parts the list row does not carry.
   *
   * Runs even on a cache hit: the copy on screen is good enough to work with
   * immediately, and this replaces it with the server's a moment later.
   *
   * The controller no longer cancels anything -- `fetchTask` shares one request
   * between everyone asking for the same task, so aborting it would cancel it
   * out from under them. It marks the result as no longer wanted here, which is
   * all this ever used it for.
   */
  useEffect(() => {
    const controller = new AbortController();

    fetchTask(taskId)
      .then((task) => {
        if (controller.signal.aborted) return;
        // Wholesale, which loses a checklist item added in the round trip this
        // reply is the end of -- the reply describes the task as it was before
        // it. Deliberate: the window is one request on a panel that has just
        // opened, the item is safely on the server and comes back on the next
        // open, and the alternative is another layered-overlay mechanism like
        // `addedSubtasks` below for a race that much smaller.
        setSaved(task);
        setLoadError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) return;
        // Gone for good: keeping it would serve a deleted task to the next open.
        if (requestError instanceof ApiError && requestError.status === 404) evictTask(taskId);
        setLoadError(requestError);
      });

    return () => controller.abort();
  }, [taskId]);

  /**
   * Keep the cache in step with whatever this panel last believed.
   *
   * One place rather than a `putTask` beside every `setSaved`: the task is set
   * from a load, from a field save, from a prerequisite change and from an
   * attachment upload, and the one that gets forgotten is what makes a cache
   * serve something stale.
   */
  useEffect(() => {
    if (saved) putTask(saved);
  }, [saved]);

  /**
   * A field edit, shown before it is saved.
   *
   * The controls are driven by the task rather than by drafts of their own, so
   * writing straight through left a picked due date sitting on its old value
   * for the whole round trip -- and a `<input type="date">` visibly snapping
   * back to yesterday is worse than a spinner. The patch goes into `pending`
   * and is laid over the task for rendering; the reply then either replaces the
   * task outright or, if it failed, leaves the old value to reappear as the
   * keys drop out of `pending`.
   */
  async function save(patch: Partial<TaskInput>) {
    setPending((current) => ({ ...current, ...patch }));
    setInFlight((count) => count + 1);
    setError(null);
    const stamp = (latestSave.current += 1);
    // Task extends TaskSummary, so the list row behind the panel takes the same
    // optimistic value with no mapping -- otherwise the row lags the panel.
    if (base) onTaskChanged(withPatch(base, patch, assignees, tasks));

    try {
      const { task: updated } = await updateTask(taskId, patch);
      // Overlapping saves can answer out of order, and the older reply knows
      // nothing of the newer edit -- applying it would undo that field.
      if (stamp === latestSave.current) {
        setSaved(updated);
        onTaskChanged(updated);
      }
    } catch (saveError: unknown) {
      setError(saveError instanceof Error ? saveError.message : 'Could not save this task.');
      // Put the row back to what is actually stored. Anything still in flight
      // re-applies itself when it lands.
      if (base) onTaskChanged(base);
    } finally {
      // Only this patch's keys: an edit to another field may still be out, and
      // has to stay on screen until its own reply arrives.
      setPending((current) => {
        const next = { ...current };
        for (const key of Object.keys(patch)) delete next[key as keyof TaskInput];
        return next;
      });
      setInFlight((count) => count - 1);
    }
  }

  async function changePrerequisites(action: 'add' | 'remove', prerequisiteId: string) {
    setError(null);
    try {
      const { task: updated } =
        action === 'add'
          ? await addPrerequisite(taskId, prerequisiteId)
          : await removePrerequisite(taskId, prerequisiteId);
      setSaved(updated);
      onTaskChanged(updated);
    } catch (changeError: unknown) {
      setError(
        changeError instanceof Error ? changeError.message : 'Could not change the prerequisites.',
      );
    }
  }

  /**
   * Add a subtask without waiting for it.
   *
   * The page posts it and puts the row in the list behind the panel; this only
   * has to show the same task in the section it belongs to, under the same
   * pending id, and swap it for the real one when it lands. A failure takes the
   * entry back out and offers Try again right here -- the page's own banner is
   * behind the scrim while this panel is open, so it could not be clicked.
   */
  function addSubtask(title: string) {
    setFailedSubtask(null);
    // Reported here rather than on the page: the page's banner is behind this
    // panel's scrim, which takes every click outside the panel.
    const { pendingId, result } = createInBackground(
      { title, parentId: taskId },
      { reportFailure: false },
    );

    setAddedSubtasks((entries) => [...entries, { id: pendingId, title, status: 'TODO' }]);

    void result.then((created) => {
      setAddedSubtasks((entries) =>
        created
          ? entries.map((entry) =>
              entry.id === pendingId
                ? { id: created.id, title: created.title, status: created.status }
                : entry,
            )
          : entries.filter((entry) => entry.id !== pendingId),
      );
      if (!created) setFailedSubtask(title);
    });
  }

  async function remove() {
    try {
      await deleteTask(taskId);
      evictTask(taskId);
      // Said before the panel closes, because the panel closing is the only
      // other thing that happens and that is also what Cancel looks like.
      toast(`Deleted “${task?.title ?? 'task'}”`);
      onTaskDeleted(taskId);
      onClose();
    } catch (deleteError: unknown) {
      setError(deleteError instanceof Error ? deleteError.message : 'Could not delete this task.');
    }
  }

  /**
   * The best copy of this task available right now, in preference order: what
   * the server last confirmed, then the list row the panel was opened from,
   * then a detail cached by an earlier open.
   *
   * The middle one is what lets the panel open filled in. `Task` is
   * `TaskSummary` plus five relation keys, so the row that was just clicked
   * already answers for the title, description, tags, status, priority, dates
   * and people -- everything except the relation sections near the bottom.
   */
  const cached = getCachedTask(taskId);
  const loaded: Task | null = saved ?? (seed ? hydrateSeed(seed, cached) : (cached ?? null));

  /**
   * The same task with the counts its sections have since reported folded in.
   *
   * Folded here rather than only at render because `save` pushes this up to the
   * list row: a total the time log worked out has to travel with the next edit,
   * or the row goes back to the count it was fetched with.
   */
  const base: Task | null = loaded && {
    ...loaded,
    trackedSeconds: counts.trackedSeconds ?? loaded.trackedSeconds,
    commentCount: counts.commentCount ?? loaded.commentCount,
  };

  /**
   * Whether the relation keys on `base` can be believed.
   *
   * An empty `subtasks` reads the same whether the task has none or the detail
   * has not landed, so the difference cannot be recovered from the task itself
   * -- and the sections below need it to choose between "nothing here" and a
   * placeholder.
   */
  const hydrated = saved !== null || cached !== undefined;

  /**
   * What the panel renders: the task above with the in-flight edits laid over
   * it. Everything below reads this rather than `base`, so an edit appears the
   * moment it is made.
   */
  const task = base ? withPatch(base, pending, assignees, tasks) : null;
  const saving = inFlight > 0;

  const notFound = loadError instanceof ApiError && loadError.status === 404;
  const headerTitle = task?.title ?? seed?.title ?? 'Task';

  /**
   * The parent, as a breadcrumb. Every row the server sends now carries its own
   * `parent`, so this is right from the first frame; the lookup behind it is
   * what still covers an optimistic row, which has the id its creator chose but
   * no title for it until the create resolves.
   */
  const parentRef: TaskRef | null =
    task?.parent ??
    (task?.parentId ? (tasks.find((candidate) => candidate.id === task.parentId) ?? null) : null);

  /**
   * Subtasks, taken from the list until the detail arrives: a parent's children
   * are exactly the rows pointing at it, and the list is already holding them.
   * Additions made from this panel are layered on top, minus any the detail has
   * since accounted for.
   */
  const knownSubtasks: TaskRef[] = hydrated
    ? (task?.subtasks ?? [])
    : tasks.filter((candidate) => candidate.parentId === taskId);
  const subtaskRefs: TaskRef[] = [
    ...knownSubtasks,
    ...addedSubtasks.filter((entry) => !knownSubtasks.some((known) => known.id === entry.id)),
  ];

  /**
   * How many rows are known to exist but are not in hand yet.
   *
   * The counts ride along on the list row, so each section can be drawn at its
   * final height before the detail arrives and nothing below it moves when it
   * does. Subtasks subtract what the list already supplied, which is normally
   * all of them -- a filtered list is the only reason this is not zero.
   */
  const missingSubtasks = hydrated ? 0 : Math.max(0, (task?.subtaskCount ?? 0) - subtaskRefs.length);
  const missingPrerequisites = hydrated ? 0 : (task?.prerequisiteCount ?? 0);
  const missingAttachments = hydrated ? 0 : (task?.attachmentCount ?? 0);
  const missingChecklist = hydrated ? 0 : (task?.checklistCount ?? 0);

  const openPrerequisites = task?.prerequisites.filter((entry) => entry.status !== 'DONE') ?? [];
  /** Before the names are in hand the count still is, and "waiting on 2 earlier
   *  tasks" is most of what the banner exists to say. */
  const blockedCount = hydrated
    ? openPrerequisites.length
    : (task?.incompletePrerequisiteCount ?? 0);

  const prerequisiteOptions = tasks.filter(
    (candidate) =>
      candidate.id !== taskId &&
      !(task?.prerequisites ?? []).some((entry) => entry.id === candidate.id),
  );

  return (
    <>
      <PanelHeader onClose={onClose} closeLabel="Close task">
        <div className="min-w-0 flex-1">
          {parentRef ? (
            <Link
              to={hrefFor(parentRef.id)}
              className="text-meta text-stone-500 hover:text-stone-700"
            >
              ← {parentRef.title}
            </Link>
          ) : null}

          {task ? (
            <EditableTitle id={titleId} title={task.title} onSave={(title) => void save({ title })} />
          ) : (
            <h2 id={titleId} className="text-panel-title font-semibold tracking-tight text-stone-900">
              {headerTitle}
            </h2>
          )}
        </div>

        {saving ? <span className="mt-1 shrink-0 text-meta text-stone-500">Saving…</span> : null}
        {task ? (
          <span className="mt-0.5 shrink-0">
            <TaskStatusBadge status={task.status} />
          </span>
        ) : null}
      </PanelHeader>

      {/* overscroll-contain rather than a body scroll lock: the page scroller is
          Layout's <main>, so setting overflow on document.body would do nothing
          at all here. */}
      <div className="flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-3">
        {error ? <Alert>{error}</Alert> : null}

        {notFound ? (
          <p className="text-sm text-stone-600">
            This task no longer exists. It may have been deleted in another tab.
          </p>
        ) : !task ? (
          // Nothing to draw it from: a deep link to a task the list is not
          // holding, with no cached copy either.
          loadError ? (
            <Alert>
              {loadError instanceof Error ? loadError.message : 'Could not load this task.'}
            </Alert>
          ) : (
            <SkeletonRows count={6} rowClassName="h-7" />
          )
        ) : (
          <>
            {/* A refresh that failed over a copy already on screen. Said
                quietly rather than replacing the panel: everything here still
                works, it is only possibly a little behind. */}
            {loadError ? (
              <Alert>
                {loadError instanceof Error ? loadError.message : 'Could not refresh this task.'}
              </Alert>
            ) : null}

            {blockedCount > 0 ? (
              // A warning, never a block: the API happily marks this done, and
              // saying so is more useful than refusing.
              <Alert tone="warning" icon={<BlockedIcon className="h-4 w-4" />}>
                <span className="text-dense">
                  Waiting on{' '}
                  {hydrated ? (
                    <>
                      {openPrerequisites.map((entry, index) => (
                        <span key={entry.id}>
                          {index > 0 ? ', ' : ''}
                          <Link to={hrefFor(entry.id)} className="underline">
                            {entry.title}
                          </Link>
                        </span>
                      ))}
                      .
                    </>
                  ) : (
                    // The names are a round trip away; the count came with the
                    // row, and it is most of what this line is here to say.
                    `${blockedCount} earlier task${blockedCount === 1 ? '' : 's'}.`
                  )}
                </span>
              </Alert>
            ) : null}

            <EditableDescription
              description={task.description}
              onSave={(description) => void save({ description })}
            />

            <TaskFields
              idPrefix={task.id}
              values={{
                status: task.status,
                priority: task.priority,
                tags: task.tags,
                startDate: task.startDate,
                dueDate: task.dueDate,
                remindAt: task.remindAt,
                repeat: task.repeat,
                ownerId: task.owner?.id ?? null,
                collaboratorIds: task.collaborators.map((assignee) => assignee.id),
                parent: parentRef,
              }}
              tasks={tasks}
              excludeFromParent={new Set([task.id, ...descendantIds(task.id, tasks)])}
              assignees={assignees}
              tagSuggestions={tagSuggestions}
              onChange={(patch) => void save(patch)}
              onParentChange={(picked) => void save({ parentId: picked?.id ?? null })}
              nextInstanceHref={task.nextInstanceId ? hrefFor(task.nextInstanceId) : null}
            />

            <section>
              <h3 className="text-dense font-semibold tracking-tight text-stone-700">Prerequisites</h3>
              {task.prerequisites.length > 0 ? (
                <ul className="mt-1.5 space-y-0.5">
                  {task.prerequisites.map((entry) => (
                    <li key={entry.id} className="flex items-center gap-2 text-dense">
                      <Link
                        to={hrefFor(entry.id)}
                        className={`min-w-0 flex-1 truncate hover:underline ${
                          entry.status === 'DONE' ? 'text-stone-500 line-through' : 'text-stone-800'
                        }`}
                      >
                        {entry.title}
                      </Link>
                      <span className="shrink-0 text-meta text-stone-500">
                        {TASK_STATUS_LABELS[entry.status]}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove prerequisite ${entry.title}`}
                        className="shrink-0 rounded p-0.5 text-stone-500 transition hover:bg-red-50 hover:text-red-700"
                        onClick={() => void changePrerequisites('remove', entry.id)}
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : hydrated ? (
                // One muted line rather than nothing. Every section in this
                // panel used to render a heading, a control, and a gap where
                // its contents would be -- which reads as "still loading", not
                // as "there are none".
                <p className="mt-1.5 text-dense text-stone-500">Nothing is blocking this task.</p>
              ) : null}

              <SkeletonRows count={missingPrerequisites} />

              <div className="mt-1.5">
                <TaskCombobox
                  compact
                  label="Add a prerequisite"
                  options={prerequisiteOptions}
                  placeholder="Blocked by…"
                  onSelect={(picked) => void changePrerequisites('add', picked.id)}
                />
              </div>
            </section>

            <section>
              <h3 className="text-dense font-semibold tracking-tight text-stone-700">Subtasks</h3>
              {subtaskRefs.length > 0 ? (
                <ul className="mt-1.5 space-y-0.5">
                  {subtaskRefs.map((entry) =>
                    // Still saving: no id to open yet, so it is text rather than
                    // a link, exactly as the list behind the panel shows it.
                    isPendingTask(entry.id) ? (
                      <li key={entry.id} className="flex items-center gap-2 text-dense">
                        <span className="min-w-0 flex-1 truncate text-stone-500">{entry.title}</span>
                        <span className="shrink-0 text-meta text-stone-500">Saving…</span>
                      </li>
                    ) : (
                      <li key={entry.id} className="flex items-center gap-2 text-dense">
                        <Link
                          to={hrefFor(entry.id)}
                          className={`min-w-0 flex-1 truncate hover:underline ${
                            entry.status === 'DONE'
                              ? 'text-stone-500 line-through'
                              : 'text-stone-800'
                          }`}
                        >
                          {entry.title}
                        </Link>
                        <span className="shrink-0 text-meta text-stone-500">
                          {TASK_STATUS_LABELS[entry.status]}
                        </span>
                      </li>
                    ),
                  )}
                </ul>
              ) : hydrated ? (
                <p className="mt-1.5 text-dense text-stone-500">No subtasks yet.</p>
              ) : null}

              <SkeletonRows count={missingSubtasks} />

              <div className="mt-1.5">
                <TaskComposer
                  placeholder="Add a subtask"
                  onSubmit={(title) => addSubtask(title)}
                />

                {failedSubtask ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-red-700">
                    <span className="min-w-0 flex-1 truncate">
                      “{failedSubtask}” was not saved.
                    </span>
                    <Button onClick={() => addSubtask(failedSubtask)}>Try again</Button>
                    <Button variant="ghost" onClick={() => setFailedSubtask(null)}>
                      Discard
                    </Button>
                  </div>
                ) : null}
              </div>
            </section>

            <TaskChecklist
              taskId={task.id}
              items={task.checklist}
              placeholderCount={missingChecklist}
              onChange={(update) => {
                // The updater is applied twice, to two different bases, because
                // the two consumers want different things -- see below. It is a
                // pure function of the array it is handed, so running it twice
                // is safe.
                setSaved((current) => {
                  // `current ?? loaded` because an item can be added before the
                  // detail request lands, when `saved` is still null: a plain
                  // `current ? ... : current` would drop that add on the floor.
                  const source = current ?? loaded;
                  if (!source) return current;
                  const checklist = update(source.checklist);
                  return {
                    ...source,
                    checklist,
                    checklistCount: checklist.length,
                    checklistDoneCount: checklist.filter((entry) => entry.done).length,
                  };
                });

                // Pushed straight to the row behind the panel rather than folded
                // into `counts` and carried by the next `save`, which is what the
                // comment and attachment counts do. Ticking is the most frequent
                // thing anyone does in this panel, and a stale "0/5" is visible
                // the moment the panel closes -- the attachment count has the
                // same hole today and is left alone.
                if (base) {
                  const checklist = update(base.checklist);
                  onTaskChanged({
                    ...base,
                    checklist,
                    checklistCount: checklist.length,
                    checklistDoneCount: checklist.filter((entry) => entry.done).length,
                  });
                }
              }}
            />

            <TaskAttachments
              taskId={task.id}
              attachments={task.attachments}
              placeholderCount={missingAttachments}
              onChange={(attachments: TaskAttachment[]) =>
                setSaved((current) =>
                  current
                    ? { ...current, attachments, attachmentCount: attachments.length }
                    : current,
                )
              }
            />
          </>
        )}

        {/* Outside the gate above, and below the sections that do need the
            detail. Both of these fetch on mount and need nothing but the id,
            so sitting inside it made their requests queue behind a reply they
            never read -- two round trips before the panel was populated where
            one would do. They now load alongside the detail. */}
        {notFound ? null : (
          <>
            <TaskTimeLog
              taskId={taskId}
              onTotalChange={(trackedSeconds) =>
                setCounts((current) => ({ ...current, trackedSeconds }))
              }
            />

            <TaskComments
              taskId={taskId}
              onCountChange={(commentCount) =>
                setCounts((current) => ({ ...current, commentCount }))
              }
            />
          </>
        )}

        {/* Inline rather than window.confirm, which blocks the page -- the
            same choice ProjectForm makes. */}
        {task ? (
          <div className="border-t border-stone-200 pt-3">
            {confirmingDelete ? (
              // `flex-wrap`, the same as PanelFooter: a sentence and two buttons
              // do not fit on one line in a panel this narrow, and without it
              // they squeeze rather than wrap.
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-dense text-stone-600">
                  Delete this task?
                  {task.subtaskCount > 0
                    ? ` Its ${task.subtaskCount} subtask${
                        task.subtaskCount === 1 ? '' : 's'
                      } will be kept.`
                    : ''}
                </span>
                <Button compact variant="danger" onClick={() => void remove()}>
                  Delete
                </Button>
                <Button compact variant="ghost" onClick={() => setConfirmingDelete(false)}>
                  Keep
                </Button>
              </div>
            ) : (
              <Button compact variant="ghost" onClick={() => setConfirmingDelete(true)}>
                Delete task
              </Button>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}

/**
 * A `TaskInput` patch expressed in the task's own shape, so the panel can show
 * an edit before the server has agreed to it.
 *
 * A key left out is unchanged and `null` clears, matching the PATCH the same
 * patch is about to make. The id-valued keys are resolved against the very
 * lists the pickers offered, so a lookup only misses for someone deleted
 * mid-edit -- and then it falls back to what the task already holds rather than
 * blanking the field.
 */
function withPatch(
  task: Task,
  patch: Partial<TaskInput>,
  assignees: Assignee[],
  tasks: TaskSummary[],
): Task {
  const next: Task = { ...task };

  if (patch.title !== undefined) next.title = patch.title;
  if (patch.description !== undefined) next.description = patch.description;
  if (patch.tags !== undefined) next.tags = patch.tags;
  if (patch.status !== undefined) next.status = patch.status;
  if (patch.priority !== undefined) next.priority = patch.priority;
  if (patch.startDate !== undefined) next.startDate = patch.startDate;
  if (patch.dueDate !== undefined) next.dueDate = patch.dueDate;
  if (patch.remindAt !== undefined) next.remindAt = patch.remindAt;
  if (patch.repeat !== undefined) next.repeat = patch.repeat;

  if (patch.ownerId !== undefined) {
    next.owner = patch.ownerId
      ? (assignees.find((assignee) => assignee.id === patch.ownerId) ?? task.owner)
      : null;
  }

  if (patch.collaboratorIds !== undefined) {
    const ids = patch.collaboratorIds;
    next.collaborators = ids
      .map(
        (id) =>
          assignees.find((assignee) => assignee.id === id) ??
          task.collaborators.find((assignee) => assignee.id === id),
      )
      .filter((assignee): assignee is Assignee => assignee !== undefined);
  }

  // The server drops the owner from the collaborators; mirroring it here keeps
  // the two controls from disagreeing for the length of a round trip.
  const ownerId = next.owner?.id;
  if (ownerId) next.collaborators = next.collaborators.filter((entry) => entry.id !== ownerId);

  if (patch.parentId !== undefined) {
    next.parentId = patch.parentId;
    const picked = tasks.find((candidate) => candidate.id === patch.parentId);
    next.parent = picked
      ? { id: picked.id, title: picked.title, status: picked.status }
      : task.parent?.id === patch.parentId
        ? task.parent
        : null;
  }

  return next;
}

/** Click the heading to edit it. Enter commits, Escape reverts -- and marks the
 *  event handled so the panel's own Escape listener does not also close it. */
function EditableTitle({
  id,
  title,
  onSave,
}: {
  id: string;
  title: string;
  onSave: (title: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);

  useEffect(() => setDraft(title), [title]);

  function commit() {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== title) onSave(trimmed);
    else setDraft(title);
  }

  if (!editing) {
    return (
      <h2 id={id}>
        <button
          type="button"
          title="Click to rename"
          // A ring rather than a tint. The tint was `hover:bg-slate-50` painted
          // onto a `bg-slate-50/60` header -- so the one control in the panel
          // that has to advertise itself was the one whose hover state was
          // invisible. Same two-stage idea the task row's priority cell uses:
          // at rest it is just the heading, hovered it is plainly a control.
          className="-mx-1 w-[calc(100%+0.5rem)] rounded-md px-1 py-0.5 text-left text-panel-title font-semibold tracking-tight text-stone-900 transition hover:bg-white hover:ring-1 hover:ring-inset hover:ring-stone-300"
          onClick={() => setEditing(true)}
        >
          {title}
        </button>
      </h2>
    );
  }

  return (
    <Input
      compact
      autoFocus
      value={draft}
      aria-label="Task title"
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          setDraft(title);
          setEditing(false);
        }
      }}
    />
  );
}

function EditableDescription({
  description,
  onSave,
}: {
  description: string | null;
  onSave: (description: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(description ?? '');

  useEffect(() => setDraft(description ?? ''), [description]);

  if (!editing) {
    return (
      <button
        type="button"
        className="w-full rounded-md px-2 py-1 text-left text-dense hover:bg-stone-50"
        onClick={() => setEditing(true)}
      >
        {description ? (
          // Plain text rather than a rich-text dependency; newlines are kept.
          <span className="whitespace-pre-wrap text-stone-800">{description}</span>
        ) : (
          <span className="text-stone-500">Add a description…</span>
        )}
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <Textarea
        compact
        autoFocus
        rows={3}
        value={draft}
        aria-label="Description"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return;
          event.preventDefault();
          setDraft(description ?? '');
          setEditing(false);
        }}
      />
      <div className="flex gap-2">
        <Button
          compact
          variant="primary"
          onClick={() => {
            setEditing(false);
            const trimmed = draft.trim();
            if (trimmed !== (description ?? '')) onSave(trimmed || null);
          }}
        >
          Save
        </Button>
        <Button
          compact
          variant="ghost"
          onClick={() => {
            setDraft(description ?? '');
            setEditing(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
